import { createInertiaApp, router } from "@inertiajs/react";
import { ReactNode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ShowFlashProvider } from "@sprout/flash.tsx";
import Layout from "@components/layout";
import { InertiaErrorBoundary } from "@/errors/inertia_error_boundary.tsx";
import "@/utils/axios_config";
import i18n from "@entrypoints/i18n.ts";
import { isPostHogEnabled, getPostHogOptions } from "@/utils/posthog_config";
import { PostHogProvider, usePostHog } from "@posthog/react";

function PostHogIdentify({ currentUserId }: { currentUserId?: number }) {
  const posthog = usePostHog();
  const [userId, setUserId] = useState(currentUserId);

  useEffect(() => {
    return router.on("success", (event) => {
      const nextUserId = (event.detail.page.props as Record<string, unknown>).currentUserId as
        | number
        | undefined;
      setUserId(nextUserId);
    });
  }, []);

  useEffect(() => {
    if (posthog && userId) {
      posthog.identify(String(userId));
    }
  }, [posthog, userId]);

  return null;
}

function PostHogWrapper({
  currentUserId,
  children,
}: {
  currentUserId?: number;
  children: ReactNode;
}) {
  if (!isPostHogEnabled()) return <>{children}</>;

  return (
    <PostHogProvider apiKey={window.ENV.POSTHOG_API_KEY} options={getPostHogOptions()}>
      <PostHogIdentify currentUserId={currentUserId} />
      {children}
    </PostHogProvider>
  );
}

export type ForethoughtProps = {
  enabled?: boolean;
  apiKey?: string;
  email?: string;
  fullName?: string;
  loggedIn?: boolean;
};

function ensureForethought(pageProps: Record<string, unknown>) {
  try {
    const ft = (pageProps as { forethought?: ForethoughtProps })?.forethought;
    if (!ft?.enabled || !ft.loggedIn) return;

    if (!document.getElementById("forethought-widget-embed-script")) {
      const s = document.createElement("script");
      s.src = "https://solve-widget.forethought.ai/embed.js";
      s.id = "forethought-widget-embed-script";
      s.setAttribute("data-api-key", String(ft.apiKey || ""));
      s.setAttribute("data-ft-Email", String(ft.email || ""));
      s.setAttribute("data-ft-Full-Name", String(ft.fullName || ""));
      s.setAttribute("data-ft-Logged-In", "true");
      s.setAttribute("data-ft-workflow-tag", "my-gh");
      s.setAttribute("config-ft-greeting-message", i18n.t("forethought_greeting", { ns: "misc" }));
      s.defer = true;
      document.body.appendChild(s);
    }
  } catch (e) {
    console.error("Failed to initialize Forethought widget:", e);
  }
}

router.on("success", (event) => {
  ensureForethought(event.detail.page.props);
});

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode;
  layout?: (page: ReactNode) => ReactNode;
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.netlify.app/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  progress: { includeCSS: false },

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // @ts-expect-error Inertia pages have a layout property
    page.default.layout =
      // @ts-expect-error Inertia pages have a layout property
      page.default.layout || ((content: ReactNode) => <Layout>{content}</Layout>);
    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      const currentUserId = (props.initialPage.props as Record<string, unknown>).currentUserId as
        | number
        | undefined;
      createRoot(el).render(
        <PostHogWrapper currentUserId={currentUserId}>
          <ShowFlashProvider>
            <InertiaErrorBoundary>
              <App {...props} />
            </InertiaErrorBoundary>
          </ShowFlashProvider>
        </PostHogWrapper>,
      );

      // ensure integrations exist on initial load too
      // @ts-expect-error router.page is not typed here
      const initialProps = (router?.page?.props || {}) as Record<string, unknown>;
      ensureForethought(initialProps);
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
